/**
 * Catches a render error anywhere below it, so one broken tool shows a message
 * with a way back instead of a blank page. The technical detail stays behind a
 * disclosure — it is for whoever gets the screenshot, not for the editor.
 *
 * `page` keeps the failure inside one page of the shell: the other pages stay
 * mounted, with their running jobs and their edits. That matters most after a
 * deploy, when a page opened for the first time asks for a chunk of the old
 * build that the server no longer has — one shared boundary used to replace
 * the whole app over it.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'

import StartScreen, { StartError } from './StartScreen'

interface Props {
    children: ReactNode
    /** Show the failure in the page's own area, under the shell. */
    page?: boolean
}

interface State {
    error: Error | null
    componentStack: string | null
}

/** Chromium, Firefox and Safari's words for a module that could not be loaded. */
const STALE_CHUNK = /dynamically imported module|Importing a module script failed|error loading dynamically imported module/i

/** Whether `error` is a page's code missing from the server: the app was updated. */
function isStaleChunk(error: Error): boolean {
    return STALE_CHUNK.test(error.message)
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = { error: null, componentStack: null }

    public static getDerivedStateFromError(error: Error): Partial<State> {
        return { error }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo)
        this.setState({ componentStack: errorInfo.componentStack ?? null })
    }

    public render() {
        const { error, componentStack } = this.state
        if (!error) return this.props.children

        const detail = `${String(error)}${componentStack ?? ''}`
        if (!this.props.page) {
            return (
                <StartScreen>
                    <StartError title="Da ist etwas schiefgelaufen." detail={detail}>
                        Die Seite konnte nicht angezeigt werden. Neu laden hilft meistens; Eingaben, die noch nicht
                        exportiert wurden, gehen dabei verloren.
                    </StartError>
                </StartScreen>
            )
        }
        return (
            <div className="flex h-full items-center justify-center overflow-y-auto bg-canvas p-6">
                {isStaleChunk(error) ? (
                    <StartError title="Eine neue Version ist da." detail={detail}>
                        Die App wurde seit dem Öffnen aktualisiert, deshalb lässt sich diese Seite nicht laden. Bitte
                        neu laden. Die anderen Seiten bleiben bis dahin, wie sie sind; was dort noch nicht exportiert
                        ist, geht beim Neuladen verloren.
                    </StartError>
                ) : (
                    <StartError title="Da ist etwas schiefgelaufen." detail={detail}>
                        Diese Seite konnte nicht angezeigt werden; die anderen Seiten bleiben, wie sie sind. Neu laden
                        hilft meistens; Eingaben, die noch nicht exportiert wurden, gehen dabei verloren.
                    </StartError>
                )}
            </div>
        )
    }
}

export default ErrorBoundary
